import { useState } from "react";
import { api } from "../api.js";
import { useApp } from "../app.jsx";
import { Button, ErrorPanel, Field, Fields, Input, Select, Textarea } from "../ui.jsx";
import { FlowStacked } from "../components/Logo.jsx";

/**
 * First run for a brand-new sign-up: bootstrap answered `needs_onboarding` and
 * there is no organisation to scope anything to, so this screen replaces the
 * whole shell.
 *
 * It collects only what an invoice legally needs — who is billing, from where,
 * and under what tax registration. Everything else has a sensible default in the
 * organizations table and can be filled in later under Settings, so this stays
 * one screen rather than a wizard.
 */

const TAX_LABELS = [
  { value: "HST", label: "HST" },
  { value: "GST", label: "GST" },
  { value: "VAT", label: "VAT" },
  { value: "Sales tax", label: "Sales tax" },
  { value: "", label: "None — I don't charge tax" },
];

// The symbol travels with the code: the PDF prints the symbol, reports group by
// the code, and letting them drift produces invoices that read "€1,200 CAD".
const CURRENCIES = [
  { code: "CAD", symbol: "$", label: "CAD — Canadian dollar" },
  { code: "USD", symbol: "$", label: "USD — US dollar" },
  { code: "EUR", symbol: "€", label: "EUR — Euro" },
  { code: "GBP", symbol: "£", label: "GBP — Pound sterling" },
];

const DEFAULT_RATE = { HST: 13, GST: 5, VAT: 20, "Sales tax": 0, "": 0 };

export default function Onboarding() {
  const { reloadApp } = useApp();

  const [form, setForm] = useState({
    name: "",
    legal_name: "",
    address: "",
    email: "",
    phone: "",
    tax_label: "HST",
    tax_rate: "13",
    tax_number: "",
    currency_code: "CAD",
    currency_symbol: "$",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const noTax = form.tax_label === "";

  const pickTaxLabel = (value) =>
    setForm((f) => ({
      ...f,
      tax_label: value,
      // Only volunteer a rate while the field is still untouched or zeroed, so
      // switching label never silently rewrites a rate someone typed.
      tax_rate:
        f.tax_rate === "" || Number(f.tax_rate) === 0 || Number(f.tax_rate) === DEFAULT_RATE[f.tax_label]
          ? String(DEFAULT_RATE[value] ?? 0)
          : f.tax_rate,
    }));

  const pickCurrency = (code) => {
    const c = CURRENCIES.find((x) => x.code === code) || CURRENCIES[0];
    setForm((f) => ({ ...f, currency_code: c.code, currency_symbol: c.symbol }));
  };

  async function submit(e) {
    e?.preventDefault();
    setError(null);

    if (!form.name.trim()) {
      setError(new Error("What is the business called? A name is required."));
      return;
    }

    setSaving(true);
    try {
      await api.post("organizations", {
        name: form.name.trim(),
        legal_name: form.legal_name.trim(),
        address: form.address,
        email: form.email.trim(),
        phone: form.phone.trim(),
        tax_label: form.tax_label,
        tax_rate: noTax ? 0 : Number(form.tax_rate) || 0,
        tax_number: form.tax_number.trim(),
        currency_code: form.currency_code,
        currency_symbol: form.currency_symbol,
      });
      // The shell is still holding a bootstrap that says "no organisation".
      // Reloading it is what swaps this screen out for the real app.
      reloadApp();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="gate">
      <form className="box" style={{ maxWidth: 560, textAlign: "left" }} onSubmit={submit}>
        {/* Cream card, so the stacked lockup takes the light pair: charcoal + rust. */}
        <div className="lock">
          <FlowStacked size={104} tone="light" />
        </div>

        <div className="eyebrow" style={{ textAlign: "center" }}>
          Set up your business
        </div>
        <p className="dim sm" style={{ marginTop: 8, marginBottom: 22, textAlign: "center" }}>
          This is the letterhead your clients will see at the top of every invoice. You can change any of
          it later under Settings.
        </p>

        {error && <ErrorPanel error={error} />}

        <Fields>
          <Field label="Business name" hint="The name clients know you by — it heads every invoice.">
            <Input
              value={form.name}
              autoFocus
              placeholder="Northwind Studio"
              onChange={(e) => set("name", e.target.value)}
            />
          </Field>

          <Field label="Registered legal name" hint="Leave blank if it's the same.">
            <Input
              value={form.legal_name}
              placeholder="2639054 Ontario Inc."
              onChange={(e) => set("legal_name", e.target.value)}
            />
          </Field>

          <Field label="Address" hint="One line per row — printed exactly as you type it.">
            <Textarea rows={3} value={form.address} onChange={(e) => set("address", e.target.value)} />
          </Field>

          <Fields cols={2}>
            <Field label="Billing email">
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </Field>
          </Fields>

          <Fields cols={2}>
            <Field label="Tax label">
              <Select value={form.tax_label} onChange={(e) => pickTaxLabel(e.target.value)}>
                {TAX_LABELS.map((t) => (
                  <option key={t.label} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Tax rate %">
              <Input
                className="i num"
                inputMode="decimal"
                value={noTax ? "0" : form.tax_rate}
                disabled={noTax}
                onChange={(e) => set("tax_rate", e.target.value)}
              />
            </Field>
          </Fields>

          <Field
            label="Tax registration number"
            hint="In Canada, a GST/HST registrant must show this number on any invoice of $30 or more — without it your client cannot claim the input tax credit."
          >
            <Input
              value={form.tax_number}
              placeholder="12345 6789 RT0001"
              onChange={(e) => set("tax_number", e.target.value)}
            />
          </Field>

          <Field label="Currency">
            <Select value={form.currency_code} onChange={(e) => pickCurrency(e.target.value)}>
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label} ({c.symbol})
                </option>
              ))}
            </Select>
          </Field>
        </Fields>

        <Button
          kind="pri"
          type="submit"
          busy={saving}
          style={{ width: "100%", justifyContent: "center", marginTop: 22 }}
        >
          {saving ? "Opening the books…" : "Open the books"}
        </Button>

        <p className="tiny muted" style={{ marginTop: 14, textAlign: "center" }}>
          You'll be the owner of this business. Invoice numbering starts at INV-1001 and prefixes,
          reminders and your logo are all waiting for you in Settings.
        </p>
      </form>
    </div>
  );
}
